import {FC, useCallback, useMemo, useState} from 'react';
import {getDatesDifferenceInSeconds} from '../../../../hooks/useTimer';
import {calculateAndFormatTime} from '../../helpers';
import {AlertSettingsProps} from '../../../settings/alert_alarm/types';
import {ProcessTask} from '../../types';
import dayjs from 'dayjs';
import {getState} from '../../../common/time_progress_bar/helpers/getState';
import {
  ReactComponent as AlertIcon
} from '../../../../assets/images/task-alert.svg';
import {
  ReactComponent as DefaultIcon
} from '../../../../assets/images/task-default.svg';
import {
  ReactComponent as ExpiredIcon
} from '../../../../assets/images/task-expired.svg';
import {
  ReactComponent as CanceledIcon
} from '../../../../assets/images/cancelled-task.svg';
import {ProgressBarState} from '../../../../constants/timeProgressBar';
import {ProcessCode} from '../../../processes/types';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import LiveTimeProgressBar from '../../../common/time_progress_bar/LiveTimeProgressBar';
import {useAppSelector} from '../../../../store/hooks';
import permissionsInfo from '../../../../constants/permissionsInfo';
import {currentUserProps} from '../../../../store/currentUser/currentUser.slice';
import {RootState} from '../../../../store/store';
import CompletedTaskPopup from './CompletedTaskPopup';

type TaskRowElementProps = {
    task: ProcessTask,
    taskAlertData: Nullable<AlertSettingsProps>
};

const TASK_LIST_VIEW_PERMISSION = permissionsInfo.tasks.view[0];
const TASK_GROUP_VIEW_PERMISSION = permissionsInfo.tasks.group.view[0];

const CompletedTask: FC<TaskRowElementProps> = ({
  task, taskAlertData
}) => {
  const {
    id: taskId,
    type:
        {description: description},
    status:
        {code: statusCode},
    targetTime,
    startedAt,
    completedAt,
    groups: taskGroups
  } = task;

  const currentTimeString = dayjs().toString();
  const {t} = useTranslation('processes');
  const {permissions: currentUserPermissions} =
    useAppSelector(({permissions}) => permissions);
  const currentUser: currentUserProps = useAppSelector(
      (state: RootState) => state.user
  );

  const {groups: userGroups = []} = currentUser || {};
  const [isOpen, setIsOpen] = useState(false);
  const currentTime = startedAt && completedAt ?
  getDatesDifferenceInSeconds(startedAt, completedAt) :
  getDatesDifferenceInSeconds(startedAt, currentTimeString);
  const isCurrentlyRunning = false;
  const isStatusCancelled = statusCode === ProcessCode.CANCELLED;

  const hasPermission = useCallback((permission) => {
    return currentUserPermissions.includes(permission);
  }, [currentUserPermissions]);

  const hasGroupIntersection = useMemo(() => {
    return !!(taskGroups?.filter((group) =>
      userGroups?.includes(group.slug)).length);
  }, [taskGroups, userGroups]);

  const isTaskAvailableToView = hasPermission(TASK_LIST_VIEW_PERMISSION) ||
    (hasPermission(TASK_GROUP_VIEW_PERMISSION) && hasGroupIntersection);

  const alertRange = useMemo(() => {
    if (!taskAlertData) return 0;

    const {relative, relativeRange, absoluteRange} = taskAlertData;

    return relative ?
      Math.floor(targetTime / relativeRange) :
      absoluteRange;
  }, [JSON.stringify(taskAlertData)]);

  const {state: timeStatus, color} = getState({
    currentTime, alertRange, targetTime, isCurrentlyRunning
  });

  const goalTime = useMemo(() => {
    return calculateAndFormatTime(targetTime);
  }, [targetTime]);

  const completedInTime = useMemo(() => {
    const formattedGoalTime =
        calculateAndFormatTime(currentTime);
    return formattedGoalTime || '';
  }, [currentTime]);

  const getIcon = () => {
    if (isStatusCancelled) {
      return (<CanceledIcon width={18} height={18}/>);
    }
    switch (timeStatus) {
      case ProgressBarState.ALERT:
        return <AlertIcon />;
      case ProgressBarState.EXPIRED:
        return <ExpiredIcon />;
      default:
        return <DefaultIcon />;
    }
  };

  const togglePopupVisibility = useCallback(() => {
    if (!isTaskAvailableToView) return;

    setIsOpen((prevState) => !prevState);
  }, [isTaskAvailableToView]);

  return (
    <>
      <div
        className={classNames('task-row-element', {'canceled': isStatusCancelled})}
        onClick={togglePopupVisibility} >
        <div className={`task-row-element_time-wrapper ${color}`}>
          <div className="task-row-element_icon-wrapper">
            {getIcon()}
            <span className="completed-in">{!isStatusCancelled && completedInTime}</span>
          </div>
          <span className="goal-time">{t('processTaskGoal')}{goalTime}</span>
        </div>
        <div className="progress-bar-wrapper">
          {!isStatusCancelled &&
            (<LiveTimeProgressBar
              startedAt={startedAt}
              completedAt={completedAt}
              targetTime={targetTime}
              alertSettings={taskAlertData}
              shouldShowExpiredIcon={false}
            />)}
        </div>
        <p
          className="task-row-element_name"
          title={description || ''}>
          {description || ''}
        </p>
      </div>

      <CompletedTaskPopup
        isOpen={isOpen}
        taskId={taskId}
        taskAlertData={taskAlertData}
        setIsOpen={setIsOpen}
      />
    </>
  );
};

export default CompletedTask;
